import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Plus, Trash2, Check, X, Loader2, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import { cardVaultService } from '../services/api';
import { toast } from 'sonner';

const SavedCards = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCard, setSavingCard] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [cardTokenizing, setCardTokenizing] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);
  const [deletingCard, setDeletingCard] = useState(null);

  // Card form for PagBank SDK
  const [cardForm, setCardForm] = useState({
    number: '',
    cvv: '',
    expiryMonth: '',
    expiryYear: '',
    holderName: '',
    holderCPF: '',
    holderBirthDate: '',
    holderPhone: '',
    nickname: '',
  });

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(' ').slice(0, 19);
  };

  // Format expiry
  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return digits.slice(0, 2) + '/' + digits.slice(2, 4);
    }
    return digits;
  };

  // Load saved cards
  const loadSavedCards = async () => {
    try {
      const cards = await cardVaultService.list();
      setSavedCards(cards);
    } catch (e) {
      console.error('Failed to load saved cards:', e);
      toast.error('Erro ao carregar cartões salvos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSavedCards();
  }, []);

  // Handle tokenizing and saving a new card via PagBank SDK
  const handleAddCard = async (e) => {
    e.preventDefault();
    
    if (!cardForm.number || !cardForm.cvv || !cardForm.expiryMonth || !cardForm.expiryYear || 
        !cardForm.holderName || !cardForm.holderCPF) {
      toast.error('Preencha todos os campos do cartão');
      return;
    }

    // Validate expiry
    const month = parseInt(cardForm.expiryMonth);
    const year = parseInt('20' + cardForm.expiryYear);
    const now = new Date();
    if (month < 1 || month > 12) {
      toast.error('Mês de expiração inválido');
      return;
    }
    if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
      toast.error('Cartão expirado');
      return;
    }

    setCardTokenizing(true);
    setSavingCard(true);

    try {
      // Step 1: Tokenize card via PagBank SDK
      const { createCardToken } = await import('../services/pagbankSDK');
      const pagbankCardToken = await createCardToken({
        number: cardForm.number.replace(/\s/g, ''),
        cvv: cardForm.cvv,
        expiryMonth: cardForm.expiryMonth,
        expiryYear: '20' + cardForm.expiryYear,
        holderName: cardForm.holderName,
        holderCPF: cardForm.holderCPF.replace(/\D/g, ''),
      });

      // Step 2: Detect brand
      let brand = 'visa';
      const firstDigits = cardForm.number.replace(/\s/g, '').slice(0, 6);
      if (firstDigits.startsWith('5') || firstDigits.startsWith('2')) brand = 'mastercard';
      else if (firstDigits.startsWith('4')) brand = 'visa';
      else if (firstDigits.startsWith('3')) brand = 'amex';
      else if (firstDigits.startsWith('6')) brand = 'elo';
      else if (firstDigits.startsWith('606282') || firstDigits.startsWith('637095')) brand = 'hipercard';

      // Step 3: Send to backend
      const cardData = {
        pagbank_card_token: pagbankCardToken,
        brand,
        last_four: cardForm.number.replace(/\s/g, '').slice(-4),
        expiry_month: month,
        expiry_year: year,
        holder_name: cardForm.holderName,
        nickname: cardForm.nickname || undefined,
      };

      await cardVaultService.tokenize(cardData);
      toast.success('Cartão salvo com sucesso!');

      // Reset form and close modal
      setShowAddCardModal(false);
      setCardForm({
        number: '', cvv: '', expiryMonth: '', expiryYear: '',
        holderName: '', holderCPF: '', holderBirthDate: '', holderPhone: '', nickname: '',
      });

      // Reload cards
      await loadSavedCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erro ao salvar cartão');
    } finally {
      setCardTokenizing(false);
      setSavingCard(false);
    }
  };

  // Set card as default
  const handleSetDefault = async (cardToken) => {
    setSettingDefault(cardToken);
    try {
      await cardVaultService.setDefault(cardToken);
      toast.success('Cartão definido como padrão');
      await loadSavedCards();
    } catch (error) {
      toast.error('Erro ao definir cartão padrão');
    } finally {
      setSettingDefault(null);
    }
  };

  // Delete card
  const handleDeleteCard = async (cardToken) => {
    if (!window.confirm('Tem certeza que deseja excluir este cartão?')) return;
    
    setDeletingCard(cardToken);
    try {
      await cardVaultService.delete(cardToken);
      toast.success('Cartão excluído');
      await loadSavedCards();
    } catch (error) {
      toast.error('Erro ao excluir cartão');
    } finally {
      setDeletingCard(null);
    }
  };

  // Get brand icon/color
  const getBrandStyle = (brand) => {
    const styles = {
      visa: { color: '#1A1F71', bg: '#E8ECFF', label: 'VISA' },
      mastercard: { color: '#EB001B', bg: '#FFF0F0', label: 'MASTERCARD' },
      elo: { color: '#003366', bg: '#E0EBF5', label: 'ELO' },
      amex: { color: '#006FCF', bg: '#E0F0FA', label: 'AMEX' },
      hipercard: { color: '#FF6600', bg: '#FFF4E8', label: 'HIPERCARD' },
    };
    return styles[brand?.toLowerCase()] || styles.visa;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <TopBar title="Meus Cartões" showBack onBack={() => navigate('/profile')} />
      
      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Add Card Button */}
        <button
          onClick={() => setShowAddCardModal(true)}
          className="w-full p-4 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Adicionar Cartão
        </button>

        {/* Saved Cards List */}
        {savedCards.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-border p-8 text-center">
            <CreditCard className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold text-lg mb-2">Nenhum cartão salvo</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Adicione um cartão para fazer pagamentos com 1 clique
            </p>
            <button
              onClick={() => setShowAddCardModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold"
            >
              Adicionar Primeiro Cartão
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {savedCards.map((card) => {
              const brandStyle = getBrandStyle(card.brand);
              return (
                <div
                  key={card.card_token}
                  className="bg-white rounded-xl shadow-sm border border-border overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-8 rounded flex items-center justify-center text-sm font-bold"
                          style={{ backgroundColor: brandStyle.bg, color: brandStyle.color }}
                        >
                          {brandStyle.label}
                        </div>
                        <div>
                          <p className="font-semibold">
                            •••• {card.last_four}
                            {card.nickname && (
                              <span className="ml-2 text-sm text-muted-foreground">({card.nickname})</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {card.holder_name} • Expira {card.expiry_month.toString().padStart(2, '0')}/{card.expiry_year.toString().slice(-2)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Default badge */}
                      {card.is_default && (
                        <div className="flex items-center gap-1 text-primary text-sm font-semibold">
                          <Star className="w-4 h-4 fill-current" />
                          Padrão
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {!card.is_default && (
                        <button
                          onClick={() => handleSetDefault(card.card_token)}
                          disabled={settingDefault === card.card_token}
                          className="flex-1 py-2 px-3 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors"
                        >
                          {settingDefault === card.cardToken ? (
                            <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                          ) : (
                            'Definir como Padrão'
                          )}
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDeleteCard(card.card_token)}
                        disabled={deletingCard === card.card_token}
                        className="flex-1 py-2 px-3 border border-destructive text-destructive rounded-lg text-sm font-medium hover:bg-destructive/5 disabled:opacity-50 transition-colors"
                      >
                        {deletingCard === card.card_token ? (
                          <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mx-auto" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm text-primary">
          <p className="font-semibold mb-2">Como funciona:</p>
          <ul className="space-y-1 text-xs">
            <li>• Seus dados de cartão são tokenizados via PagBank (o número nunca toca nosso servidor)</li>
            <li>• Use "1-Click" no checkout para pagar instantaneamente</li>
            <li>• Pode definir um cartão como padrão para agilizar ainda mais</li>
            <li>• Gerencie seus cartões aqui a qualquer momento</li>
          </ul>
        </div>
      </div>

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAddCardModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Outfit'] text-xl font-bold">Adicionar Cartão</h2>
              <button onClick={() => setShowAddCardModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Insira os dados do cartão. O número e CVV são coletados de forma segura pelo PagBank.
            </p>

            <form onSubmit={handleAddCard} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Número do Cartão</label>
                <input
                  type="text"
                  value={cardForm.number}
                  onChange={(e) => setCardForm({...cardForm, number: formatCardNumber(e.target.value)})}
                  placeholder="1234 5678 9012 3456"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary font-mono text-lg"
                  maxLength={19}
                  required
                  autoComplete="cc-number"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Validade</label>
                  <input
                    type="text"
                    value={cardForm.expiryMonth + (cardForm.expiryYear ? '/' + cardForm.expiryYear : '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      const month = val.slice(0, 2);
                      const year = val.slice(2, 4);
                      setCardForm({...cardForm, expiryMonth: month, expiryYear: year});
                    }}
                    placeholder="MM/AA"
                    className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={5}
                    required
                    autoComplete="cc-exp"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">CVV</label>
                  <input
                    type="text"
                    value={cardForm.cvv}
                    onChange={(e) => setCardForm({...cardForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                    placeholder="123"
                    className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={4}
                    required
                    autoComplete="cc-csc"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nome no Cartão</label>
                <input
                  type="text"
                  value={cardForm.holderName}
                  onChange={(e) => setCardForm({...cardForm, holderName: e.target.value.toUpperCase()})}
                  placeholder="JOÃO SILVA"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoComplete="cc-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">CPF do Titular</label>
                <input
                  type="text"
                  value={cardForm.holderCPF}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setCardForm({...cardForm, holderCPF: val});
                  }}
                  placeholder="123.456.789-00"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Apelido (opcional)</label>
                <input
                  type="text"
                  value={cardForm.nickname}
                  onChange={(e) => setCardForm({...cardForm, nickname: e.target.value})}
                  placeholder="Cartão Principal"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddCardModal(false)}
                  className="flex-1 py-2 border border-border rounded-xl font-semibold text-secondary hover:bg-muted"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingCard || cardTokenizing}
                  className="flex-1 py-2 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cardTokenizing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {cardTokenizing ? 'Tokenizando...' : savingCard ? 'Salvando...' : 'Salvar Cartão'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BottomNav currentPath="/profile" />
    </div>
  );
};

export default SavedCards;